// Bilag fra mobilkameraet er 3-12 MB. Hele den fila ble lastet opp raa, og paa
// mobilnett er det titalls sekunder for bilaget i det hele tatt finnes i
// innboksen. Det er den ventetiden brukeren opplever som «treg opplasting» —
// ikke analysen, som allerede gaar i bakgrunnen.
//
// En kvittering trenger ikke tolv megapiksler. 2200 piksler paa lengste kant er
// mer enn OCR-en og Vision-modellen leser, og gir typisk 300-600 KB. Da er
// opplastingen sekunder, og backend slipper aa avvise bilaget paa
// RECEIPT_ATTACHMENT_MAX_BYTES (10 MB) etterpaa.
//
// Alt her er beste innsats. Klarer ikke nettleseren aa dekode fila — HEIC uten
// stotte, canvas som ikke gir blob, en PDF — lastes originalen opp som for.
// Ingen sti her har lov til aa stoppe en opplasting.
(() => {
  const MAX_EDGE = 2200;
  const JPEG_QUALITY = 0.82;
  // Under denne grensen er det ingenting aa hente: en liten kvittering laster opp
  // paa et blunk, og en ny JPEG-runde ville bare kostet skarphet.
  const MIN_BYTES = 700 * 1024;
  // En omkoding som knapt sparer noe er ikke verdt tapet. Da beholdes originalen.
  const KEEP_ORIGINAL_ABOVE = 0.85;
  // PDF og GIF rores ikke: PDF er ikke et bilde canvas kan tegne, og en GIF ville
  // mistet alt utenom forste bilde.
  const RE_ENCODABLE = /^image\/(jpeg|jpg|png|webp|heic|heif|avif|tiff)$/i;

  const isReEncodable = (file) => RE_ENCODABLE.test(String(file?.type || ''));

  const jpegName = (name) => {
    const base = String(name || '').replace(/\.[^.]+$/, '').trim();
    return `${base || 'bilag'}.jpg`;
  };

  const noRelease = () => {};

  // `createImageBitmap` dekoder utenfor hovedtraaden og handterer HEIC der
  // nettleseren kan det. `<img>` er reserven for nettlesere uten den.
  //
  // EXIF-rotasjonen maa folge med: et portrettbilde fra mobilen ligger paa siden
  // i selve bytene, og uten `from-image` ville vi lagret kvitteringen liggende og
  // bedt OCR-en lese den slik. Eldre implementasjoner kjenner ikke valget, og da
  // proever vi uten.
  const decodeImage = async (file) => {
    const createBitmap = globalThis.createImageBitmap;
    if (typeof createBitmap === 'function') {
      for (const options of [{ imageOrientation: 'from-image' }, undefined]) {
        try {
          const bitmap = await createBitmap(file, options);
          return { image: bitmap, release: () => bitmap.close?.() };
        } catch (error) { /* neste forsok, saa <img> under */ }
      }
    }
    if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return null;
    const url = URL.createObjectURL(file);
    try {
      const image = await new Promise((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('image-decode-failed'));
        element.src = url;
      });
      return { image, release: () => URL.revokeObjectURL(url) };
    } catch (error) {
      URL.revokeObjectURL(url);
      return null;
    }
  };

  const dimensionsOf = (image) => ({
    width: Number(image?.width || image?.naturalWidth || 0),
    height: Number(image?.height || image?.naturalHeight || 0),
  });

  const encodeToJpeg = (image, width, height) => {
    if (typeof document === 'undefined') return Promise.resolve(null);
    const canvas = document.createElement('canvas');
    if (typeof canvas?.getContext !== 'function' || typeof canvas.toBlob !== 'function') return Promise.resolve(null);
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return Promise.resolve(null);
    context.drawImage(image, 0, 0, width, height);
    return new Promise((resolve) => { canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY); });
  };

  const prepareReceiptFileForUpload = async (file) => {
    try {
      if (!file || typeof File !== 'function') return file;
      if (!isReEncodable(file) || !(Number(file.size) > MIN_BYTES)) return file;
      const decoded = await decodeImage(file);
      if (!decoded) return file;
      try {
        const { width, height } = dimensionsOf(decoded.image);
        if (!width || !height) return file;
        const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
        const blob = await encodeToJpeg(
          decoded.image,
          Math.max(1, Math.round(width * scale)),
          Math.max(1, Math.round(height * scale)),
        );
        if (!blob || !(blob.size > 0) || blob.size > file.size * KEEP_ORIGINAL_ABOVE) return file;
        return new File([blob], jpegName(file.name), {
          type: 'image/jpeg',
          lastModified: file.lastModified || Date.now(),
        });
      } finally {
        (decoded.release || noRelease)();
      }
    } catch (error) {
      return file;
    }
  };

  globalThis.PlummeReceiptImage = Object.freeze({
    MAX_EDGE,
    JPEG_QUALITY,
    MIN_BYTES,
    isReEncodable,
    prepareReceiptFileForUpload,
  });
})();
